import { useEffect, useMemo, useState } from "react";
import { RangeCard } from "../components/RangeCard";
import { listEvents } from "../lib/rangegridApi";

function formatMoney(amountCents = 0, currency = "usd") {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency
  }).format(Number(amountCents || 0) / 100);
}

function formatDate(value) {
  return value ? new Date(value).toLocaleDateString() : "Not recorded";
}

function monthKey(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(date = new Date()) {
  return new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" }).format(date);
}

function eventDateKey(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function compactCalendarTimeParts(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const hour = hours % 12 || 12;
  return {
    label: minutes ? `${hour}:${String(minutes).padStart(2, "0")}` : String(hour),
    period: hours >= 12 ? "PM" : "AM"
  };
}

function eventTimeRange(event = {}) {
  const start = compactCalendarTimeParts(event.startsAt);
  const end = compactCalendarTimeParts(event.endsAt);
  if (!start && !end) {
    return "";
  }
  if (start && end && start.period === end.period) {
    return `${start.label}-${end.label} ${end.period}`;
  }
  if (start && end) {
    return `${start.label} ${start.period}-${end.label} ${end.period}`;
  }
  const only = start || end;
  return `${only.label} ${only.period}`;
}

const INTERNAL_SCHEDULE_COPY = /(?:chapter lead to owner|owner\/chapter|owner handoff|handoff note|workflow verification|participant notes|reaudit|demo|internal|test event|audit)/i;

function displayScheduleTitle(title = "") {
  const cleanTitle = String(title || "").trim();
  if (!cleanTitle) {
    return "Untitled class";
  }
  if (!INTERNAL_SCHEDULE_COPY.test(cleanTitle)) {
    return cleanTitle;
  }
  return cleanTitle
    .replace(/^Demo\s+/i, "")
    .replace(/\bHandoff Drill\b/i, "Member Skills Session")
    .replace(/\bLead Reaudit\b/i, "Chapter Practice Session")
    .replace(/\bLead Final\b/i, "Chapter Training Session")
    .replace(/\s+\d{4}-\d{2}-\d{2}(?:\s+\d+)?$/i, "")
    .trim();
}

function scheduledChapterLabel(event = {}, chapters = []) {
  if (event.chapterSnapshot?.name) {
    return event.chapterSnapshot.name;
  }
  if (event.chapterId) {
    const chapter = chapters.find((item) => item.chapterId === event.chapterId);
    return chapter?.name || "Selected chapter";
  }
  if (Array.isArray(event.chapterIds) && event.chapterIds.length > 0) {
    const names = event.chapterIds
      .map((chapterId) => chapters.find((item) => item.chapterId === chapterId)?.name)
      .filter(Boolean);
    return names.length > 0 ? names.join(", ") : `${event.chapterIds.length} chapters`;
  }
  return "All chapters";
}

function scheduleStatus(event = {}, now = new Date()) {
  if (event.status === "pending_approval" || event.approval?.status === "pending") {
    return "Needs approval";
  }
  const startsAt = new Date(event.startsAt);
  const endsAt = new Date(event.endsAt || event.startsAt);
  if (Number.isNaN(startsAt.getTime())) {
    return "Scheduled";
  }
  if (!Number.isNaN(endsAt.getTime()) && endsAt < now) {
    return "Past";
  }
  if (startsAt <= now && (!Number.isNaN(endsAt.getTime()) && endsAt >= now)) {
    return "In progress";
  }
  return "Upcoming";
}

function buildMonthDays(monthDate = new Date()) {
  const start = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const gridStart = new Date(start);
  gridStart.setDate(start.getDate() - start.getDay());
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + index);
    return date;
  });
}

function receiptCategory(receipt = {}) {
  if (receipt.donationId || receipt.type === "donation") {
    return "donations";
  }
  if (receipt.eventId || receipt.registrationId || receipt.type === "event_registration") {
    return "events";
  }
  if (receipt.membershipTypeId || receipt.memberId || receipt.type === "membership") {
    return "memberships";
  }
  return "other";
}

function sumPaidReceipts(receipts, category) {
  return receipts
    .filter((receipt) => receipt.status === "paid")
    .filter((receipt) => !category || receiptCategory(receipt) === category)
    .reduce((total, receipt) => total + Number(receipt.amountCents || 0), 0);
}

function memberIsActive(member = {}) {
  const effectiveStatus = member.expiration?.effectiveStatus || member.status;
  const reviewStatus = member.reviewSummary?.reviewStatus || member.review?.status;
  return effectiveStatus === "active" && (!reviewStatus || reviewStatus === "approved");
}

function isNonPublicMemberRecord(member = {}) {
  const email = String(member.email || "").toLowerCase();
  const name = String(member.fullName || "").toLowerCase();
  const source = String(member.source || "").toLowerCase();
  return /(?:@dev\.rangegrid\.local|rangegrid-.*audit|owner-audit|test@|example\.com)/i.test(email)
    || /(?:demo|audit|test)/i.test(name)
    || /(?:codex|audit|test)/i.test(source);
}

function readinessWithLocalSignals(readiness, { businessHoursConfigured = false } = {}) {
  if (!readiness?.checkpoints?.length || !businessHoursConfigured) {
    return readiness;
  }
  const checkpoints = readiness.checkpoints.map((checkpoint) => (
    checkpoint.key === "business_hours"
      ? { ...checkpoint, complete: true, detail: "Operating schedule is saved." }
      : checkpoint
  ));
  const complete = checkpoints.filter((checkpoint) => checkpoint.complete).length;
  return {
    ...readiness,
    checkpoints,
    ready: checkpoints.every((checkpoint) => checkpoint.complete),
    summary: {
      ...(readiness.summary || {}),
      complete,
      total: checkpoints.length
    }
  };
}

function paymentRecordKeys(record = {}) {
  return [
    record.receiptId,
    record.checkoutSessionId,
    record.paymentIntentId,
    record.subscriptionId,
    record.customerId && record.amountCents && record.createdAt
      ? `${record.customerId}:${record.amountCents}:${new Date(record.createdAt).toISOString().slice(0, 10)}`
      : ""
  ].filter(Boolean);
}

function uniqueRecentPaymentRecords(receipts = [], events = []) {
  const seenReceiptKeys = new Set();
  const receiptRecords = receipts
    .filter((receipt) => {
      const keys = paymentRecordKeys(receipt);
      const existing = keys.some((key) => seenReceiptKeys.has(key));
      keys.forEach((key) => seenReceiptKeys.add(key));
      return !existing;
    })
    .map((receipt) => ({ ...receipt, dashboardRecordType: "receipt" }));

  const eventRecords = events
    .filter((event) => paymentRecordKeys(event).every((key) => !seenReceiptKeys.has(key)))
    .map((event) => ({ ...event, dashboardRecordType: "event" }));

  return [...receiptRecords, ...eventRecords]
    .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
    .slice(0, 4);
}

function statusLabel(value) {
  return String(value || "not ready")
    .replace(/[._-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function paymentRecordTypeLabel(value) {
  const labels = {
    "membership_signup_intent.fulfilled": "Membership payment fulfilled",
    "membership_signup_intent.created": "Membership application started",
    "payment.checkout_completed": "Checkout completed",
    "event_registration.payment_fulfilled": "Event payment fulfilled",
    membership_purchase: "Membership receipt",
    event_registration: "Event registration receipt",
    resource_reservation: "Reservation receipt"
  };
  return labels[String(value || "").trim()] || statusLabel(value);
}

function paymentRecordStatusLabel(value) {
  const labels = {
    not_required: "No payment required",
    paid: "Paid",
    fulfilled: "Fulfilled",
    pending: "Pending",
    failed: "Failed",
    refunded: "Refunded",
    cancelled: "Cancelled",
    canceled: "Cancelled"
  };
  const key = String(value || "").trim().toLowerCase();
  return labels[key] || statusLabel(value || "Recorded");
}

function titleCase(value) {
  return statusLabel(value);
}

const DEMO_MEMBER_PORTALS = [
  {
    name: "Lox & Loaded",
    href: "/member/r/lox-loaded",
    detail: "Chapter membership, Saint Paul classes, documents, payments, and support."
  },
  {
    name: "Liberty Long Range",
    href: "/member/r/liberty-long-range",
    detail: "Physical range membership, orientation, reservations, payments, and range support."
  }
];

function normalizeClockTime(value, fallback = "09:00") {
  return /^\d{2}:\d{2}$/.test(String(value || "")) ? value : fallback;
}

function formatSunOffset(value) {
  const minutes = Number.parseInt(value || "0", 10);
  if (!Number.isFinite(minutes) || minutes === 0) {
    return "";
  }
  const sign = minutes > 0 ? "+" : "-";
  return ` ${sign} ${Math.abs(minutes)} min`;
}

function formatPeriodPoint(period = {}, point) {
  const modeKey = point === "opens" ? "opensMode" : "closesMode";
  const offsetKey = point === "opens" ? "opensOffsetMinutes" : "closesOffsetMinutes";
  const fallbackTime = point === "opens" ? "09:00" : "17:00";
  const mode = period[modeKey] || "fixed";
  if (mode === "sunrise" || mode === "sunset") {
    return `${titleCase(mode)}${formatSunOffset(period[offsetKey])}`;
  }
  return normalizeClockTime(period[point], fallbackTime);
}

function dayOfYear(date) {
  const start = Date.UTC(date.getUTCFullYear(), 0, 0);
  const diff = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) - start;
  return Math.floor(diff / 86400000);
}

function normalizeDegrees(value) {
  return ((value % 360) + 360) % 360;
}

function degreesToRadians(value) {
  return value * (Math.PI / 180);
}

function radiansToDegrees(value) {
  return value * (180 / Math.PI);
}

function timezoneOffsetMinutes(date, timezone) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false
    }).formatToParts(date).reduce((map, part) => {
      map[part.type] = part.value;
      return map;
    }, {});
    const localAsUtc = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour === "24" ? "0" : parts.hour),
      Number(parts.minute),
      Number(parts.second)
    );
    return Math.round((localAsUtc - date.getTime()) / 60000);
  } catch {
    return -date.getTimezoneOffset();
  }
}

function minutesToClockLabel(totalMinutes) {
  const minutes = ((Math.round(totalMinutes) % 1440) + 1440) % 1440;
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function resolveSolarEvent(dateInput, timezone, latitude, longitude, eventType, offsetMinutes = 0) {
  const lat = Number.parseFloat(latitude);
  const lng = Number.parseFloat(longitude);
  if (!dateInput || !Number.isFinite(lat) || !Number.isFinite(lng)) {
    return "";
  }

  const date = new Date(`${dateInput}T12:00:00Z`);
  const zenith = 90.833;
  const n = dayOfYear(date);
  const lngHour = lng / 15;
  const isSunrise = eventType === "sunrise";
  const t = n + ((isSunrise ? 6 : 18) - lngHour) / 24;
  const meanAnomaly = (0.9856 * t) - 3.289;
  let trueLong = meanAnomaly
    + (1.916 * Math.sin(degreesToRadians(meanAnomaly)))
    + (0.020 * Math.sin(degreesToRadians(2 * meanAnomaly)))
    + 282.634;
  trueLong = normalizeDegrees(trueLong);

  let rightAscension = radiansToDegrees(Math.atan(0.91764 * Math.tan(degreesToRadians(trueLong))));
  rightAscension = normalizeDegrees(rightAscension);
  const trueLongQuadrant = Math.floor(trueLong / 90) * 90;
  const rightAscensionQuadrant = Math.floor(rightAscension / 90) * 90;
  rightAscension = (rightAscension + (trueLongQuadrant - rightAscensionQuadrant)) / 15;

  const sinDec = 0.39782 * Math.sin(degreesToRadians(trueLong));
  const cosDec = Math.cos(Math.asin(sinDec));
  const cosHour = (
    Math.cos(degreesToRadians(zenith)) - (sinDec * Math.sin(degreesToRadians(lat)))
  ) / (cosDec * Math.cos(degreesToRadians(lat)));

  if (cosHour > 1 || cosHour < -1) {
    return "";
  }

  let hourAngle = isSunrise
    ? 360 - radiansToDegrees(Math.acos(cosHour))
    : radiansToDegrees(Math.acos(cosHour));
  hourAngle /= 15;

  const localMeanTime = hourAngle + rightAscension - (0.06571 * t) - 6.622;
  const utcMinutes = (((localMeanTime - lngHour) % 24) + 24) % 24 * 60;
  const utcDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, utcMinutes));
  const offset = timezoneOffsetMinutes(utcDate, timezone || "America/Los_Angeles");
  return minutesToClockLabel(utcMinutes + offset + Number.parseInt(offsetMinutes || "0", 10));
}

function dateKeyForTimezone(date, timezone) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).formatToParts(date).reduce((map, part) => {
    map[part.type] = part.value;
    return map;
  }, {});
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function dayKeyForTimezone(date, timezone) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    weekday: "long"
  }).format(date).toLowerCase();
}

function formatResolvedPeriodPoint(period = {}, point, businessHoursForm = {}, dateKey) {
  const modeKey = point === "opens" ? "opensMode" : "closesMode";
  const offsetKey = point === "opens" ? "opensOffsetMinutes" : "closesOffsetMinutes";
  const mode = period[modeKey] || "fixed";
  if (mode === "sunrise" || mode === "sunset") {
    const resolved = resolveSolarEvent(
      dateKey,
      businessHoursForm.timezone || "America/Los_Angeles",
      businessHoursForm.sun?.latitude,
      businessHoursForm.sun?.longitude,
      mode,
      period[offsetKey] || 0
    );
    return resolved || formatPeriodPoint(period, point);
  }
  return formatPeriodPoint(period, point);
}

function todayOperatingHoursLabel(businessHoursForm = {}, labels = {}) {
  const timezone = businessHoursForm.timezone || "America/Los_Angeles";
  const now = new Date();
  const dayKey = dayKeyForTimezone(now, timezone);
  const dateKey = dateKeyForTimezone(now, timezone);
  const day = businessHoursForm.weeklyHours?.[dayKey];
  if (!day) {
    return { value: "Not set", detail: `Set ${labels.scheduleScope || "range-wide"} hours in ${labels.setupLabel || "Range Setup"}.` };
  }
  if (day.closed) {
    return { value: "Closed today", detail: titleCase(dayKey) };
  }
  const period = day.periods?.[0];
  if (!period) {
    return { value: "No open window", detail: titleCase(dayKey) };
  }
  return {
    value: `${formatResolvedPeriodPoint(period, "opens", businessHoursForm, dateKey)}-${formatResolvedPeriodPoint(period, "closes", businessHoursForm, dateKey)}`,
    detail: `${titleCase(dayKey)} ${labels.scheduleScope || "range-wide"} schedule`
  };
}

function readinessProgress(readiness) {
  if (!readiness?.summary?.total) {
    return 0;
  }
  return Math.round((Number(readiness.summary.complete || 0) / Number(readiness.summary.total || 1)) * 100);
}

function firstIncompleteCheckpoint(readiness) {
  return (readiness?.checkpoints || []).find((item) => !item.complete);
}

function checkpointPath(key, selectedRangePath) {
  const actions = {
    profile: `${selectedRangePath}/settings`,
    branding: `${selectedRangePath}/settings#settings-member-portal`,
    business_hours: `${selectedRangePath}/settings#settings-hours`,
    membership_app: `${selectedRangePath}/applications`,
    resources_app: `${selectedRangePath}/applications`,
    membership_config: `${selectedRangePath}/apps/membership#membership-requirements`,
    public_plans: `${selectedRangePath}/apps/membership#membership-plans`,
    payments: `${selectedRangePath}/payments#payments-stripe`,
    resources: `${selectedRangePath}/apps/resources#resource-inventory`
  };
  return actions[key] || selectedRangePath;
}

export function HomePage({
  businessHoursConfigured,
  businessHoursForm,
  canManageAccess,
  chapters = [],
  donationsEnabled,
  donationsSummary,
  eventsEnabled,
  events: scheduledEvents = [],
  eventsLoading = false,
  loading,
  membershipEnabled,
  membershipLoading,
  membershipSummary,
  members,
  membersLoading,
  navigate,
  notificationSummary,
  paymentEvents,
  paymentOverviewLoading,
  paymentReceipts,
  paymentSettings,
  readiness,
  readinessLoading,
  ranges,
  resourcesEnabled,
  selectedRange,
  setActiveRangeId
}) {
  const receipts = paymentReceipts?.receipts || [];
  const paymentHistoryEvents = paymentEvents?.events || [];
  const membershipTypes = membershipSummary?.membershipTypes || [];
  const memberRows = (members || []).filter((member) => !isNonPublicMemberRecord(member));
  const selectedRangePath = selectedRange ? `/console/ranges/${selectedRange.rangeId}` : "/console";
  const chapterOrganization = (
    selectedRange?.config?.templateKey === "chapter_organization" ||
    selectedRange?.creationTemplate?.templateKey === "chapter_organization" ||
    String(selectedRange?.config?.vocabulary?.unit || selectedRange?.creationTemplate?.vocabulary?.unit || "").toLowerCase() === "chapter"
  );
  const workspaceLabel = selectedRange?.config?.vocabulary?.workspace || selectedRange?.creationTemplate?.vocabulary?.workspace || (chapterOrganization ? "Organization" : "Range");
  const workspaceLower = workspaceLabel.toLowerCase();
  const operationsLabel = chapterOrganization ? "Operations" : "Range Operations";
  const facilitiesLabel = chapterOrganization ? "Facilities" : "Range Facilities";
  const setupLabel = chapterOrganization ? `${workspaceLabel} setup` : "Range setup";
  const dailyDeskLabel = chapterOrganization ? `Today's ${workspaceLabel.toLowerCase()} desk` : "Today's range desk";
  const scheduleScopeLabel = chapterOrganization ? "organization-wide" : "range-wide";
  const operationsPath = chapterOrganization
    ? `${selectedRangePath}/apps/staff-portal#staff-programs-roster`
    : `${selectedRangePath}/apps/daily-ops#operations-board`;
  const facilitiesPath = chapterOrganization
    ? `${selectedRangePath}/apps/chapters#chapters-directory`
    : `${selectedRangePath}/apps/resources#resource-inventory`;
  const operationsDetail = chapterOrganization
    ? "Review chapter program readiness, roster pressure, participant handoff, and local operating signals from one board."
    : "Check resource status, line flow, cease-fire rhythm, shooters, and equipment from one board.";
  const facilitiesDetail = chapterOrganization
    ? "Chapter meeting locations, classroom assets, baseline rules, and maintenance readiness."
    : "Range sections, bay zones, firing positions, rules, and maintenance status.";
  const [dashboardEvents, setDashboardEvents] = useState([]);
  const [dashboardEventsLoading, setDashboardEventsLoading] = useState(false);
  const activeMemberCount = memberRows.filter(memberIsActive).length;
  const totalMemberRecordCount = memberRows.length;
  const pendingReviewCount = memberRows.filter((member) => ["pending", "needs_info"].includes(member.reviewSummary?.reviewStatus || member.status)).length;
  const expiringSoonCount = memberRows.filter((member) => member.expiration?.expiringSoon).length;
  const publicPlanCount = membershipTypes.filter((type) => type.status === "active" && type.publicSignupEnabled).length;
  const activePlanCount = membershipTypes.filter((type) => type.status === "active").length;
  const paidReceiptTotal = sumPaidReceipts(receipts);
  const membershipRevenue = sumPaidReceipts(receipts, "memberships");
  const eventRevenue = sumPaidReceipts(receipts, "events");
  const donationRevenue = sumPaidReceipts(receipts, "donations");
  const stripe = paymentSettings?.stripe || {};
  const stripeReady = Boolean(stripe.connectedAccountId && stripe.chargesEnabled && stripe.payoutsEnabled);
  const stripeStatus = stripeReady
    ? "Ready"
    : stripe.connectedAccountId
      ? "Needs attention"
      : paymentSettings?.configured === false
        ? "Setup needed"
        : "Not connected";
  const scheduleReady = Boolean(businessHoursConfigured || businessHoursForm?.updatedAt);
  const scheduleAndResourcesReady = Boolean(scheduleReady && resourcesEnabled);
  const effectiveReadiness = readinessWithLocalSignals(readiness, { businessHoursConfigured: scheduleReady });
  const progress = readinessProgress(effectiveReadiness);
  const launchBlocker = firstIncompleteCheckpoint(effectiveReadiness);
  const dashboardStateLoading = Boolean(selectedRange) && (
    loading ||
    membershipLoading ||
    membersLoading ||
    paymentOverviewLoading ||
    readinessLoading ||
    paymentSettings === null ||
    readiness === null ||
    (membershipEnabled && membershipSummary === null)
  );
  const ownerDailyChecks = [
    {
      label: "Member decisions",
      value: pendingReviewCount > 0 ? `${pendingReviewCount} waiting` : "Clear",
      detail: pendingReviewCount > 0 ? "Approve, request info, or decline pending member applications." : "No member applications need a decision right now.",
      status: pendingReviewCount > 0 ? "attention" : "good",
      path: `${selectedRangePath}/apps/membership#membership-reviews`
    },
    {
      label: "Public signup",
      value: publicPlanCount > 0 ? `${publicPlanCount} plan${publicPlanCount === 1 ? "" : "s"}` : "Not live",
      detail: publicPlanCount > 0 ? "Visitors can see public membership options." : "Publish at least one public membership plan before sending prospects to the site.",
      status: publicPlanCount > 0 ? "good" : "attention",
      path: `${selectedRangePath}/apps/membership#membership-plans`
    },
    {
      label: "Online checkout",
      value: stripeStatus,
      detail: stripeReady ? "Paid membership checkout can run." : "Finish Stripe before relying on paid public signup.",
      status: stripeReady ? "good" : "attention",
      path: `${selectedRangePath}/payments#payments-stripe`
    },
    {
      label: chapterOrganization ? "Hours and chapters" : "Hours and resources",
      value: scheduleAndResourcesReady ? "Ready" : "Check setup",
      detail: scheduleAndResourcesReady
        ? (chapterOrganization ? "Operating hours and chapter program controls are available." : "Operating hours and facility controls are available.")
        : `Confirm ${workspaceLower} hours and ${chapterOrganization ? "chapter program controls" : "facilities"} before opening member-facing flows.`,
      status: scheduleAndResourcesReady ? "good" : "attention",
      path: scheduleAndResourcesReady ? facilitiesPath : `${selectedRangePath}/settings#settings-hours`
    }
  ];
  const actionQueue = dashboardStateLoading ? [] : [
    pendingReviewCount > 0 && {
      label: "Review pending member applications",
      detail: `${pendingReviewCount} member record${pendingReviewCount === 1 ? "" : "s"} need an owner decision.`,
      path: `${selectedRangePath}/apps/membership#membership-reviews`
    },
    expiringSoonCount > 0 && {
      label: "Check upcoming renewals",
      detail: `${expiringSoonCount} active member${expiringSoonCount === 1 ? "" : "s"} expire soon.`,
      path: `${selectedRangePath}/apps/membership#membership-members`
    },
    !businessHoursConfigured && {
      label: "Set operating schedule",
      detail: `Member-facing availability needs ${scheduleScopeLabel} hours.`,
      path: `${selectedRangePath}/settings#settings-hours`
    },
    membershipEnabled && publicPlanCount === 0 && {
      label: "Publish a membership plan",
      detail: "Public membership signup needs at least one active public plan.",
      path: `${selectedRangePath}/apps/membership#membership-plans`
    },
    !stripeReady && {
      label: "Finish payment readiness",
      detail: stripe.connectedAccountId ? "Stripe account requirements still need attention." : "Connect Stripe before paid checkout.",
      path: `${selectedRangePath}/payments#payments-stripe`
    },
    launchBlocker && {
      label: `Resolve launch checkpoint: ${launchBlocker.label}`,
      detail: launchBlocker.detail,
      path: checkpointPath(launchBlocker.key, selectedRangePath)
    }
  ].filter(Boolean).slice(0, 5);
  const appShortcuts = [
    { label: operationsLabel, detail: operationsDetail, path: operationsPath, enabled: chapterOrganization ? true : resourcesEnabled },
    { label: facilitiesLabel, detail: facilitiesDetail, path: facilitiesPath, enabled: chapterOrganization ? true : resourcesEnabled },
    { label: "Membership", detail: "Plans, members, requirements, reviews.", path: `${selectedRangePath}/apps/membership#membership-members`, enabled: membershipEnabled },
    { label: "Programs & Events", detail: "Classes, orientations, matches, rosters.", path: `${selectedRangePath}/apps/events#events-schedule`, enabled: eventsEnabled },
    { label: "Leads", detail: "Public update requests and launch interest.", path: `${selectedRangePath}/apps/leads`, enabled: true },
    { label: "Payments", detail: "Receipts, payment history, Stripe setup.", path: `${selectedRangePath}/payments#payments-overview`, enabled: true }
  ];
  const recentPayments = uniqueRecentPaymentRecords(receipts, paymentHistoryEvents);
  const todayHours = todayOperatingHoursLabel(businessHoursForm, {
    scheduleScope: scheduleScopeLabel,
    setupLabel
  });
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [calendarEventCursorByDay, setCalendarEventCursorByDay] = useState({});
  useEffect(() => {
    if (!selectedRange?.rangeId || !chapterOrganization || !eventsEnabled || scheduledEvents.length > 0) {
      setDashboardEvents([]);
      setDashboardEventsLoading(false);
      return undefined;
    }
    let cancelled = false;
    async function loadDashboardEvents() {
      setDashboardEventsLoading(true);
      try {
        const result = await listEvents(selectedRange.rangeId, {});
        if (!cancelled) {
          setDashboardEvents(result.events || []);
        }
      } catch {
        if (!cancelled) {
          setDashboardEvents([]);
        }
      } finally {
        if (!cancelled) {
          setDashboardEventsLoading(false);
        }
      }
    }
    loadDashboardEvents();
    return () => {
      cancelled = true;
    };
  }, [chapterOrganization, eventsEnabled, scheduledEvents.length, selectedRange?.rangeId]);

  const effectiveScheduledEvents = scheduledEvents.length > 0 ? scheduledEvents : dashboardEvents;
  const effectiveEventsLoading = eventsLoading || dashboardEventsLoading;
  const classSchedule = useMemo(() => (
    (effectiveScheduledEvents || [])
      .filter((event) => event.startsAt)
      .sort((left, right) => String(left.startsAt).localeCompare(String(right.startsAt)))
  ), [effectiveScheduledEvents]);
  const calendarDays = useMemo(() => buildMonthDays(calendarMonth), [calendarMonth]);
  const eventsByDay = useMemo(() => classSchedule.reduce((map, event) => {
    const key = eventDateKey(event.startsAt);
    if (!key) {
      return map;
    }
    const current = map.get(key) || [];
    current.push(event);
    map.set(key, current);
    return map;
  }, new Map()), [classSchedule]);
  const selectedMonthKey = monthKey(calendarMonth);
  const now = new Date();
  const calendarSeedKey = `${selectedRange?.rangeId || "none"}:${classSchedule.map((event) => event.eventId || event.startsAt).join("|")}`;
  const [seededCalendarKey, setSeededCalendarKey] = useState("");

  useEffect(() => {
    if (!chapterOrganization || classSchedule.length === 0 || seededCalendarKey === calendarSeedKey) {
      return;
    }
    const currentMonthHasClasses = classSchedule.some((event) => monthKey(new Date(event.startsAt)) === monthKey(calendarMonth));
    if (!currentMonthHasClasses) {
      const nowTime = Date.now();
      const nearestUpcoming = classSchedule.find((event) => Date.parse(event.endsAt || event.startsAt) >= nowTime);
      const nearestClass = nearestUpcoming || classSchedule[classSchedule.length - 1];
      const startsAt = new Date(nearestClass.startsAt);
      if (!Number.isNaN(startsAt.getTime())) {
        setCalendarMonth(new Date(startsAt.getFullYear(), startsAt.getMonth(), 1));
      }
    }
    setSeededCalendarKey(calendarSeedKey);
  }, [calendarMonth, calendarSeedKey, chapterOrganization, classSchedule, seededCalendarKey]);

  if (selectedRange && chapterOrganization) {
    const commandLinks = [
      {
        title: "Insights",
        path: `${selectedRangePath}/apps/insights`
      },
      {
        title: "Chapters",
        path: `${selectedRangePath}/apps/chapters`
      },
      {
        title: "Programs & events",
        path: `${selectedRangePath}/apps/events#events-schedule`
      }
    ];

    return (
      <>
        <section className="range-dashboard organization-command-dashboard">
          <section className="panel organization-command-panel">
            <div className="dashboard-title-row">
              <div>
                <span className="section-kicker">Organization owner command</span>
                <h2>{selectedRange.name}</h2>
              </div>
            </div>
            <div className="organization-command-links" aria-label="Organization owner links">
              {commandLinks.map((item) => (
                <button type="button" key={item.title} onClick={() => navigate(item.path)}>
                  <strong>{item.title}</strong>
                </button>
              ))}
            </div>
          </section>

          <section className="panel organization-calendar-panel">
            <div className="organization-calendar-header">
              <div>
                <span className="section-kicker">Monthly class calendar</span>
                <h3>{monthLabel(calendarMonth)}</h3>
              </div>
              <div className="organization-calendar-controls">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setCalendarMonth((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1))}
                >
                  Prev
                </button>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setCalendarMonth(new Date(new Date().getFullYear(), new Date().getMonth(), 1))}
                >
                  Today
                </button>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setCalendarMonth((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1))}
                >
                  Next
                </button>
              </div>
            </div>

            <div className="organization-month-grid" aria-label={`${monthLabel(calendarMonth)} class schedule`}>
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <div className="organization-weekday" key={day}>{day}</div>
              ))}
              {calendarDays.map((day) => {
                const key = eventDateKey(day);
                const dayEvents = eventsByDay.get(key) || [];
                const activeEventIndex = dayEvents.length > 0
                  ? ((calendarEventCursorByDay[key] || 0) % dayEvents.length + dayEvents.length) % dayEvents.length
                  : 0;
                const visibleEvent = dayEvents[activeEventIndex];
                const outsideMonth = monthKey(day) !== selectedMonthKey;
                const today = eventDateKey(day) === eventDateKey(now);
                return (
                  <div
                    className={`organization-calendar-day${outsideMonth ? " is-outside-month" : ""}${today ? " is-today" : ""}${dayEvents.length ? " has-events" : ""}`}
                    key={key}
                  >
                    <div className="organization-calendar-day-label">
                      <strong>{day.getDate()}</strong>
                      <span className="organization-calendar-day-badges">
                        {today && <span>Today</span>}
                      </span>
                    </div>
                    <div className="organization-calendar-events">
                      {visibleEvent && (
                        <button
                          type="button"
                          className={`organization-calendar-event is-${scheduleStatus(visibleEvent, now).toLowerCase().replace(/\s+/g, "-")}`}
                          key={visibleEvent.eventId || `${visibleEvent.title}-${visibleEvent.startsAt}`}
                          onClick={() => navigate(`${selectedRangePath}/apps/events?eventId=${encodeURIComponent(visibleEvent.eventId || "")}#events-schedule`)}
                        >
                          <span>{eventTimeRange(visibleEvent)}</span>
                          <strong>{displayScheduleTitle(visibleEvent.title)}</strong>
                          <small>{scheduledChapterLabel(visibleEvent, chapters)}</small>
                        </button>
                      )}
                      {dayEvents.length > 1 && (
                        <div className="organization-calendar-event-pager" aria-label={`${dayEvents.length} classes on ${key}`}>
                          <button
                            type="button"
                            aria-label={`Previous class on ${key}`}
                            onClick={() => setCalendarEventCursorByDay((current) => ({
                              ...current,
                              [key]: (((current[key] || 0) - 1 + dayEvents.length) % dayEvents.length)
                            }))}
                          >
                            ‹
                          </button>
                          <span>{activeEventIndex + 1}/{dayEvents.length}</span>
                          <button
                            type="button"
                            aria-label={`Next class on ${key}`}
                            onClick={() => setCalendarEventCursorByDay((current) => ({
                              ...current,
                              [key]: (((current[key] || 0) + 1) % dayEvents.length)
                            }))}
                          >
                            ›
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {effectiveEventsLoading && (
              <div className="dashboard-empty-state dashboard-loading-state">
                <strong>Loading class schedule</strong>
                <span>Checking the current program calendar.</span>
              </div>
            )}
            {!effectiveEventsLoading && classSchedule.length === 0 && (
              <div className="dashboard-empty-state">
                <strong>No classes scheduled</strong>
                <span>Add classes in Programs & events to populate the organization calendar.</span>
              </div>
            )}
          </section>
        </section>
      </>
    );
  }

  return (
    <>
      {selectedRange && (
        <section className="range-dashboard range-command-dashboard">
          <section className="panel dashboard-command-hero">
            <div className="dashboard-title-row">
              <div>
                <span className="section-kicker">Owner command</span>
                <h2>{selectedRange.name}</h2>
                <p>Start the operating day, clear member decisions, and jump into the work areas that need owner attention.</p>
              </div>
              <div className={`dashboard-posture-pill ${dashboardStateLoading ? "is-checking" : actionQueue.length === 0 ? "is-clear" : "needs-action"}`}>
                <span>Today</span>
                <strong>{dashboardStateLoading ? "Checking" : actionQueue.length === 0 ? "Clear" : `${actionQueue.length} actions`}</strong>
              </div>
            </div>

            <div className="dashboard-command-strip">
              <button type="button" onClick={() => navigate(operationsPath)}>
                <span>Open operations</span>
                <strong>{chapterOrganization ? "Program board" : "Range board"}</strong>
              </button>
              <button type="button" onClick={() => navigate(`${selectedRangePath}/apps/membership#membership-reviews`)}>
                <span>Member decisions</span>
                <strong>{pendingReviewCount}</strong>
              </button>
              <button type="button" onClick={() => navigate(`${selectedRangePath}/settings#settings-hours`)}>
                <span>Today's hours</span>
                <strong>{todayHours.value}</strong>
              </button>
              <button type="button" onClick={() => navigate(`${selectedRangePath}/payments#payments-stripe`)}>
                <span>Payments</span>
                <strong>{stripeStatus}</strong>
              </button>
            </div>
          </section>

          <div className="dashboard-ops-grid">
            <section className="panel dashboard-panel dashboard-daily-panel">
              <div className="panel-header">
                <h3>{dailyDeskLabel}</h3>
                <p>Use this as the owner's first stop before members arrive or public signup goes out.</p>
              </div>
              <button
                type="button"
                className="dashboard-daily-primary"
                disabled={chapterOrganization ? false : !resourcesEnabled}
                onClick={() => navigate(operationsPath)}
              >
                <span>{chapterOrganization ? "Live program controls" : "Live range controls"}</span>
                <strong>{(chapterOrganization || resourcesEnabled) ? `Open ${operationsLabel}` : `${operationsLabel} off`}</strong>
                <small>{operationsDetail}</small>
              </button>
              <div className="dashboard-daily-check-list">
                {ownerDailyChecks.map((check) => (
                  <button
                    type="button"
                    className={`dashboard-daily-check is-${check.status}`}
                    key={check.label}
                    onClick={() => navigate(check.path)}
                  >
                    <span>{check.label}</span>
                    <strong>{check.value}</strong>
                    <small>{check.detail}</small>
                  </button>
                ))}
              </div>
            </section>

            <section className="panel dashboard-panel dashboard-action-panel">
              <div className="panel-header">
                <h3>Owner action queue</h3>
                <p>Work these before opening more member-facing flows.</p>
              </div>
              <div className="owner-action-list">
                {dashboardStateLoading && (
                  <div className="dashboard-empty-state dashboard-loading-state">
                    <strong>Checking owner actions</strong>
                    <span>Loading membership, payment, schedule, and launch readiness before showing tasks.</span>
                  </div>
                )}
                {!dashboardStateLoading && actionQueue.map((item) => (
                  <button type="button" key={item.label} onClick={() => navigate(item.path)}>
                    <strong>{item.label}</strong>
                    <span>{item.detail}</span>
                  </button>
                ))}
                {!dashboardStateLoading && actionQueue.length === 0 && (
                  <div className="dashboard-empty-state">
                    <strong>No immediate owner actions</strong>
                    <span>Membership, schedule, launch readiness, and payment blockers are quiet.</span>
                  </div>
                )}
              </div>
            </section>
          </div>

          <div className="dashboard-grid">
            <section className="panel dashboard-panel">
              <div className="panel-header">
                <h3>Membership pipeline</h3>
                <p>People, plans, and renewal pressure without burying the owner in raw records.</p>
              </div>
              <div className="dashboard-pipeline-grid">
                <button type="button" disabled={!membershipEnabled} onClick={() => navigate(`${selectedRangePath}/apps/membership#membership-members`)}>
                  <span>Active members</span>
                  <strong>{activeMemberCount}</strong>
                  <small>{membersLoading || membershipLoading ? "Loading member health..." : `${totalMemberRecordCount} total record${totalMemberRecordCount === 1 ? "" : "s"} / ${expiringSoonCount} expiring soon`}</small>
                </button>
                <button type="button" disabled={!membershipEnabled} onClick={() => navigate(`${selectedRangePath}/apps/membership#membership-reviews`)}>
                  <span>Pending review</span>
                  <strong>{pendingReviewCount}</strong>
                  <small>Applications and requirement reviews.</small>
                </button>
                <button type="button" disabled={!membershipEnabled} onClick={() => navigate(`${selectedRangePath}/apps/membership#membership-plans`)}>
                  <span>Public plans</span>
                  <strong>{publicPlanCount}</strong>
                  <small>{activePlanCount} active plan{activePlanCount === 1 ? "" : "s"} in the catalog</small>
                </button>
              </div>
            </section>

            <section className="panel dashboard-panel">
              <div className="panel-header">
                <h3>Launch readiness</h3>
                <p>Publish checkpoints kept compact for owner review.</p>
              </div>
              <div className="dashboard-readiness-block">
                <div>
                  <strong>{readinessLoading ? "Checking" : effectiveReadiness?.ready ? "Ready" : `${progress}%`}</strong>
                  <span>{effectiveReadiness?.ready ? "All launch checkpoints are clear." : launchBlocker?.label || "No readiness data loaded."}</span>
                </div>
                <div className="launch-progress" aria-label="Launch readiness progress">
                  <span style={{ width: `${progress}%` }} />
                </div>
                <button type="button" className="secondary-button" onClick={() => navigate(launchBlocker ? checkpointPath(launchBlocker.key, selectedRangePath) : `${selectedRangePath}/applications`)}>
                  {launchBlocker ? "Open blocker" : "Workspace menu"}
                </button>
              </div>
            </section>
          </div>

            <section className="panel dashboard-panel dashboard-app-panel">
              <div className="panel-header">
              <h3>Owner work areas</h3>
              <p>Grouped by how the owner actually moves through a normal operating day.</p>
              </div>
            <div className="dashboard-app-list">
              {appShortcuts.map((item) => (
                <button type="button" key={item.label} disabled={!item.enabled} onClick={() => navigate(item.path)}>
                  <strong>{item.label}</strong>
                  <span>{item.detail}</span>
                </button>
              ))}
            </div>
          </section>

          <div className="dashboard-grid dashboard-support-grid">
            <section className="panel dashboard-panel dashboard-finance-panel">
              <div className="panel-header">
                <h3>Payment snapshot</h3>
                <p>Enough money context for the dashboard, with detailed work kept in Payments.</p>
              </div>
              <div className="finance-snapshot-grid">
                <button type="button" onClick={() => navigate(`${selectedRangePath}/payments#payments-overview`)}>
                  <span>Total recorded</span>
                  <strong>{formatMoney(paidReceiptTotal)}</strong>
                </button>
                <button type="button" onClick={() => navigate(`${selectedRangePath}/payments#payments-overview`)}>
                  <span>Membership</span>
                  <strong>{formatMoney(membershipRevenue)}</strong>
                </button>
                <button type="button" onClick={() => navigate(`${selectedRangePath}/payments#payments-overview`)}>
                  <span>Events</span>
                  <strong>{formatMoney(eventRevenue)}</strong>
                </button>
                {donationsEnabled && (
                  <button type="button" onClick={() => navigate(`${selectedRangePath}/payments#payments-overview`)}>
                    <span>Donations</span>
                    <strong>{formatMoney(donationRevenue || donationsSummary?.paidAmountCents || 0)}</strong>
                  </button>
                )}
              </div>
            </section>

            <section className="panel dashboard-panel">
              <div className="panel-header">
                <h3>Recent records</h3>
                <p>Latest payment and notification traces for quick confidence checks.</p>
              </div>
              <div className="money-movement-list compact-operator-list">
                {recentPayments.map((item) => (
                  <div key={item.receiptId || item.auditId}>
                    <strong>{item.dashboardRecordType === "receipt" ? formatMoney(item.amountCents, item.currency) : paymentRecordTypeLabel(item.type)}</strong>
                    <span>{item.dashboardRecordType === "receipt" ? `${statusLabel(receiptCategory(item))} receipt` : paymentRecordStatusLabel(item.paymentStatus || item.subscriptionStatus)}</span>
                    <small>{formatDate(item.createdAt)}</small>
                  </div>
                ))}
                {!paymentOverviewLoading && recentPayments.length === 0 && (
                  <div>
                    <strong>No recent payment records</strong>
                    <span>{notificationSummary?.queued ? `${notificationSummary.queued} notifications queued.` : "Operational records will appear as the range is used."}</span>
                    <small>Dashboard is ready for the next owner workflow.</small>
                  </div>
                )}
              </div>
            </section>
          </div>

          {canManageAccess && (
            <section className="panel dashboard-panel dashboard-admin-strip">
              <div>
                <h3>Administrative control</h3>
                <p>Owner access, application launch, and {setupLabel.toLowerCase()} stay one click away without crowding daily operations.</p>
              </div>
              <div className="dashboard-action-row">
                <button type="button" className="secondary-button" onClick={() => navigate(`${selectedRangePath}/settings`)}>
                  {setupLabel}
                </button>
                <button type="button" className="secondary-button" onClick={() => navigate(`${selectedRangePath}/applications`)}>
                  Workspace menu
                </button>
              </div>
            </section>
          )}
        </section>
      )}

      {!selectedRange && (
        <section className="service-grid">
          {DEMO_MEMBER_PORTALS.map((portal) => (
            <button type="button" className="service-tile" key={portal.href} onClick={() => navigate(portal.href)}>
              <strong>{portal.name}</strong>
              <span>{portal.detail}</span>
              <small>Open member portal</small>
            </button>
          ))}
        </section>
      )}

      {!selectedRange && (
        <section className="panel">
          <div className="widget-title-row">
            <div className="panel-header">
              <h2>Choose where to continue</h2>
              <p>Member access starts in the portal for the organization or range you want to use.</p>
            </div>
          </div>

          {loading && <div className="empty-state">Loading ranges...</div>}
          {!loading && ranges.length === 0 && (
            <div className="empty-state">
              <strong>No owner workspace yet</strong>
              <span>Create your first RangeGrid workspace to start launch setup.</span>
              <button type="button" onClick={() => navigate("/console/onboarding")}>Start RangeGrid</button>
            </div>
          )}
          {!loading && ranges.length > 0 && (
            <div className="range-list">
              {ranges.map((range) => (
                <button
                  type="button"
                  className="range-button"
                  key={range.rangeId}
                  onClick={() => {
                    setActiveRangeId(range.rangeId);
                    navigate(`/console/ranges/${range.rangeId}`);
                  }}
                >
                  <RangeCard range={range} active={range.rangeId === selectedRange?.rangeId} />
                </button>
              ))}
            </div>
          )}
        </section>
      )}
    </>
  );
}
